import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  TableRow,
  TableCell,
  IconButton,
  Checkbox,
  Select,
  MenuItem,
  TextField,
  styled,
  Box,
  Typography,
  Tooltip
} from '@mui/material';
import { Trash2, GripVertical, AlertCircle } from 'lucide-react';

interface WorkflowLabel {
  id: string;
  label_id: string;
  label: string;
  dependency: string[] | null;
  mandatory: boolean;
  start_time: string;
  end_time: string;
}

interface SortableRowProps {
  id: string;
  index: number;
  workflowItem: WorkflowLabel;
  allWorkflowItems: WorkflowLabel[];
  onDelete: (id: string) => void;
  onDependencyChange: (id: string, deps: string[]) => void;
  onMandatoryChange: (id: string, value: boolean) => void;
  onTimeChange: (id: string, type: 'start' | 'end', value: string) => void;
  isFirst: boolean;
}

const TimeTextField = styled(TextField)(({ theme, error }) => ({
  '& input': {
    padding: '8px',
    width: '80px'
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: error ? theme.palette.error.light : 'transparent',
    '&:hover': {
      backgroundColor: error ? theme.palette.error.light : 'transparent'
    }
  }
}));

const MemoizedTimeTextField = React.memo(
  ({ value, onChange, onBlur, error, placeholder }: any) => (
    <TimeTextField
      size="small"
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      error={error}
      inputProps={{
        pattern: '^[0-5]?[0-9]:[0-5][0-9]$'
      }}
    />
  ),
  (prevProps, nextProps) => {
    // Only update if these values change
    return (
      prevProps.value === nextProps.value && prevProps.error === nextProps.error
    );
  }
);

const SortableRow = React.memo(
  ({
    id,
    workflowItem,
    allWorkflowItems,
    onDelete,
    onDependencyChange,
    onMandatoryChange,
    onTimeChange,
    isFirst
  }: SortableRowProps) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({ id });

    const [startTime, setStartTime] = useState(workflowItem.start_time || '');
    const [endTime, setEndTime] = useState(workflowItem.end_time || '');
    const [startTimeError, setStartTimeError] = useState(false);
    const [endTimeError, setEndTimeError] = useState(false);
    const [startTimeErrorMessage, setStartTimeErrorMessage] = useState('');
    const [endTimeErrorMessage, setEndTimeErrorMessage] = useState('');

    useEffect(() => {
      if (workflowItem.start_time !== startTime && workflowItem.start_time) {
        setStartTime(workflowItem.start_time);
      }
      if (workflowItem.end_time !== endTime && workflowItem.end_time) {
        setEndTime(workflowItem.end_time);
      }

      if (isFirst && workflowItem.dependency === null) {
        onDependencyChange(id, []);
      }
    }, [
      workflowItem.start_time,
      workflowItem.end_time,
      isFirst,
      id,
      onDependencyChange
    ]);

    const validateTimeFormat = (timeString: string): boolean => {
      const timeRegex = /^([0-5]?[0-9]):([0-5][0-9])$/;
      return timeRegex.test(timeString);
    };

    const convertToSeconds = (timeString: string): number => {
      const [minutes, seconds] = timeString.split(':').map(Number);
      return minutes * 60 + seconds;
    };

    const validateTimePair = (start: string, end: string): boolean => {
      if (!start || !end) return false;

      const startSeconds = convertToSeconds(start);
      const endSeconds = convertToSeconds(end);

      return startSeconds < endSeconds;
    };

    const handleTimeInputChange = useCallback(
      (type: 'start' | 'end', value: string) => {
        if (type === 'start') {
          setStartTime(value);
          setStartTimeError(false);
          setStartTimeErrorMessage('');
        } else {
          setEndTime(value);
          setEndTimeError(false);
          setEndTimeErrorMessage('');
        }
      },
      []
    );

    const handleTimeBlur = useCallback(
      (type: 'start' | 'end', value: string) => {
        const isValidFormat = validateTimeFormat(value);
        let errorMessage = '';

        if (!value) {
          errorMessage = 'Time is required';
        } else if (!isValidFormat) {
          errorMessage = 'Use MM:SS format';
        }

        if (type === 'start') {
          setStartTimeError(!!errorMessage);
          setStartTimeErrorMessage(errorMessage);

          if (!errorMessage && endTime) {
            const isValidPair = validateTimePair(value, endTime);
            if (!isValidPair) {
              setStartTimeError(true);
              setStartTimeErrorMessage('Start time must be less than end time');
              return;
            }
          }
        } else {
          setEndTimeError(!!errorMessage);
          setEndTimeErrorMessage(errorMessage);

          if (!errorMessage && startTime) {
            const isValidPair = validateTimePair(startTime, value);
            if (!isValidPair) {
              setEndTimeError(true);
              setEndTimeErrorMessage(
                'End time must be greater than start time'
              );
              return;
            }
          }
        }

        if (isValidFormat) {
          onTimeChange(id, type, value);
        }
      },
      [
        id,
        startTime,
        endTime,
        validateTimeFormat,
        validateTimePair,
        onTimeChange
      ]
    );

    const handleDependencyChange = useCallback(
      (e: any) => {
        onDependencyChange(id, e.target.value);
      },
      [id, onDependencyChange]
    );

    const handleMandatoryChange = useCallback(
      (e: any) => {
        onMandatoryChange(id, e.target.checked);
      },
      [id, onMandatoryChange]
    );

    const handleDelete = useCallback(() => {
      onDelete(id);
    }, [id, onDelete]);

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1
    };

    const dependencyOptions = React.useMemo(() => {
      return allWorkflowItems
        .filter(item => item.id !== id)
        .map(item => (
          <MenuItem key={item.id} value={item.id}>
            {item.label}
          </MenuItem>
        ));
    }, [allWorkflowItems, id]);

    return (
      <TableRow ref={setNodeRef} style={style}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" {...attributes} {...listeners}>
              <GripVertical size={20} />
            </IconButton>
            <Typography>{workflowItem.label}</Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MemoizedTimeTextField
              value={startTime}
              onChange={(e: any) =>
                handleTimeInputChange('start', e.target.value)
              }
              onBlur={(e: any) => handleTimeBlur('start', e.target.value)}
              placeholder="MM:SS"
              error={startTimeError}
              inputProps={{
                pattern: '^[0-5]?[0-9]:[0-5][0-9]$'
              }}
            />
            {startTimeError && (
              <Tooltip title={startTimeErrorMessage}>
                <AlertCircle size={20} color="error" />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MemoizedTimeTextField
              value={endTime}
              onChange={(e: any) =>
                handleTimeInputChange('end', e.target.value)
              }
              onBlur={(e: any) => handleTimeBlur('end', e.target.value)}
              placeholder="MM:SS"
              error={endTimeError}
              inputProps={{
                pattern: '^[0-5]?[0-9]:[0-5][0-9]$'
              }}
            />
            {endTimeError && (
              <Tooltip title={endTimeErrorMessage}>
                <AlertCircle size={20} color="error" />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell>
          {!isFirst && (
            <Select
              multiple
              size="small"
              value={workflowItem.dependency || []}
              onChange={handleDependencyChange}
              style={{ minWidth: 120 }}
              renderValue={selected => {
                return (selected as string[])
                  .map(id => {
                    const item = allWorkflowItems.find(w => w.id === id);
                    return item ? item.label : '';
                  })
                  .join(', ');
              }}
            >
              {dependencyOptions}
            </Select>
          )}
        </TableCell>
        <TableCell align="center">
          <Checkbox
            checked={workflowItem.mandatory}
            onChange={handleMandatoryChange}
          />
        </TableCell>
        <TableCell align="center">
          <IconButton onClick={handleDelete} size="small" color="error">
            <Trash2 size={20} />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  }
  // Custom equality function for React.memo to prevent unnecessary re-renders
  // (prevProps, nextProps) => {
  //   return (
  //     prevProps.id === nextProps.id &&
  //     prevProps.isFirst === nextProps.isFirst &&
  //     prevProps.workflowItem.label === nextProps.workflowItem.label &&
  //     prevProps.workflowItem.mandatory === nextProps.workflowItem.mandatory &&
  //     prevProps.workflowItem.start_time === nextProps.workflowItem.start_time &&
  //     prevProps.workflowItem.end_time === nextProps.workflowItem.end_time &&
  //     // Check if dependency array length has changed
  //     JSON.stringify(prevProps.workflowItem.dependency) ===
  //       JSON.stringify(nextProps.workflowItem.dependency)
  //   );
  // }
);

export default SortableRow;
